import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
    Box, 
    TextField, 
    FormControlLabel, 
    Switch, 
    Autocomplete, 
    MenuItem,
    Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import municipalityAPI from "../../../Network/Municipality";
import contractorAPI from "../../../Network/Contractor";
import Cookies from "universal-cookie";

const cookies = new Cookies();

export default function RenderContent({ formData, setFormData, isEdit }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);
    
    const [municipalities, setMunicipalities] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);

    useEffect(() => {
        const fetchResources = async () => {
            setLoadingResources(true);
            const authParams = {
                username: cookies.get("user_name") ? cookies.get("user_name") : null,
                user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
                account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
                account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
                authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
                sid: cookies.get("sid") ? cookies.get("sid") : null,
                baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
                id: cookies.get("id") ? cookies.get("id") : null,
            };
            try {
                const [munRes, conRes] = await Promise.all([
                    municipalityAPI.getMunicipalityList({ params: authParams }),
                    contractorAPI.getContractorList({ params: authParams })
                ]);
                
                if (munRes?.status && munRes?.data?.status) {
                    setMunicipalities(munRes.data.data || []);
                }
                if (conRes?.status && conRes?.data?.status) {
                    setContractors(conRes.data.data || []);
                }
            } catch (error) {
                console.error("Error fetching resources:", error);
            }
            setLoadingResources(false);
        };
        fetchResources();
    }, []);

    const handleContractorChange = (event, newValue) => {
        if (typeof newValue === 'string') {
            // New contractor name entered
            setFormData({ ...formData, contractor_id: newValue });
        } else if (newValue && newValue.id) {
            // Existing contractor selected
            setFormData({ ...formData, contractor_id: newValue.id });
        } else {
            // Cleared
            setFormData({ ...formData, contractor_id: null });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <Autocomplete
                sx={{ mt: 2 }}
                options={municipalities}
                getOptionLabel={(option) => option.name || ""}
                value={municipalities.find(m => m.id === formData.municipality_id) || null}
                onChange={(event, newValue) => {
                    setFormData({ 
                        ...formData, 
                        municipality_id: newValue ? newValue.id : null,
                        municipality_name: newValue ? newValue.name : ""
                    });
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("ACCOUNTS.municipality") || "البلدية *"}
                        required
                        variant="outlined"
                        sx={{
                            "& .MuiInputLabel-root, & .MuiInputBase-input": { fontFamily: "Cairo-Medium" }
                        }}
                    />
                )}
            />

            <Autocomplete
                freeSolo
                options={contractors}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || "";
                }}
                value={contractors.find(c => c.id === formData.contractor_id) || formData.contractor_id || ""}
                onChange={handleContractorChange}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                        setFormData({ ...formData, contractor_id: newInputValue || null });
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={t("ACCOUNTS.contractor") || "المقاول (اختياري)"}
                        variant="outlined"
                        sx={{
                            "& .MuiInputLabel-root, & .MuiInputBase-input": { fontFamily: "Cairo-Medium" }
                        }}
                        placeholder={t("ACCOUNTS.contractor_placeholder") || "اختر مقاول أو ادخل اسم جديد"}
                    />
                )}
            />

            {!isEdit && (
                <TextField
                    label={t("ACCOUNTS.name") || "اسم الحساب"}
                    required
                    fullWidth
                    value={formData?.account_name || ""}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                    variant="outlined"
                    sx={{
                        "& .MuiInputLabel-root, & .MuiInputBase-input": { fontFamily: "Cairo-Medium" }
                    }}
                />
            )}

            {!isEdit && (
                <TextField
                    label={t("ACCOUNTS.username") || "اسم المستخدم"}
                    required={!isEdit}
                    disabled={isEdit}
                    fullWidth
                    value={formData?.username || ""}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    variant="outlined"
                    sx={{
                        "& .MuiInputLabel-root, & .MuiInputBase-input": { fontFamily: "Cairo-Medium" }
                    }}
                />
            )}

            {!isEdit && (
                <TextField
                    label={t("ACCOUNTS.password") || "كلمة المرور"}
                    required
                    fullWidth
                    type="password"
                    value={formData?.password || ""}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    variant="outlined"
                    sx={{
                        "& .MuiInputLabel-root, & .MuiInputBase-input": { fontFamily: "Cairo-Medium" }
                    }}
                    helperText={t("ACCOUNTS.password_hint") || "8-100 حرف، تشمل حروف كبيرة وصغيرة وأرقام ورموز"}
                />
            )}

        </Box>
    );
}
